<template>
	<div class="Modal" v-if="supportTicket">
		<div class="header">
			<div :class="currentPhase === 'NEW' ? 'primary navigation button' : 'navigation button'">NIEUW</div>
			<Button :class="currentPhase === 'IN_PROGRESS' ? 'primary navigation button' : 'navigation button'" @click="changeCurrentPhase('IN_PROGRESS')" :disabled="isLoading">IN BEHANDELING</Button>
			<Button :class="currentPhase === 'IN_SOLUTION' ? 'primary navigation button' : 'navigation button'" @click="changeCurrentPhase('IN_SOLUTION')" :disabled="isLoading">IN OPLOSSING</Button>
			<Button :class="currentPhase === 'FINISHING' || currentPhase === 'FINISHED' ? 'primary navigation button' : 'navigation button'" @click="changeCurrentPhase('FINISHING')" :disabled="isLoading">{{ currentPhase === "FINISHED" ? "AFGEROND" : "AF TE RONDEN" }}</Button>
		</div>
		<div class="body">
			<div class="details">
				<div class="detail-columns">
					<div class="detail-column">
						<div class="detail-row">
							<div class="primary row-header">SUPPORT</div>
							<div class="row-item">
								<div class="row-item-key">{{ project.company.companyname }}</div>
								<div class="row-item-value">{{ project.company.customernumber }}</div>
							</div>
							<div class="row-item">
								<div class="row-item-key">{{ project.name }}</div>
								<div class="row-item-value">{{ project.number }}</div>
							</div>
							<div class="padded row-item">
								<div class="row-item-key">Ticket aangemaakt op</div>
								<div class="row-item-value">{{ parseDate(supportTicket.created_at) }}</div>
							</div>
						</div>
						<div class="detail-row" v-if="originalAppointment != null">
							<div class="row-header">OORSPRONKELIJKE UITVOERING</div>
							<div class="row-item">
								<div class="row-item-key">Datum</div>
								<div class="row-item-value">{{ parseDate(originalAppointment.date) }}</div>
							</div>
							<div class="row-item">
								<div class="row-item-key">Tijdstip</div>
								<div class="row-item-value">{{ originalAppointment.planned_start_time.substring(0, 5) }} -
									{{ originalAppointment.planned_end_time.substring(0, 5) }}
								</div>
							</div>
							<div class="row-item">
								<div class="row-item-key">Ploeg</div>
								<div class="row-item-value">{{ originalAppointment.teamLeader.display_name }}</div>
							</div>
							<div class="row-item" v-if="originalAppointment.historicTeamMembers">
								<div class="row-item-key">Medewerkers</div>
								<div class="items">
									<div class="row-item-value" v-for="member in originalAppointment.historicTeamMembers">
										{{ member.display_name }}
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="detail-column">
						<div class="detail-vertical-row">
							<div class="button support-reason">{{ supportTicket.reason }}</div>
							<textarea disabled class="TextField">{{ supportTicket.remark }}</textarea>
							<div v-if="supportTicket.type === 'SUPPORT'" class="row-item">
								<div>Factuur op ON-HOLD zetten</div>
								<ToggleField
									:enabled="!isLoading"
									:checked="supportTicket.invoice_on_hold"
									@change="updateInvoiceOnHold"
									type="checkbox"
									name="invoiceOnHold"></ToggleField>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div class="pictures">
				<div v-for="file in files" class="upload-wrapper">
					<a :href="'/files/getById?file_id=' + file.id" target="_blank" class="image">
						<img
							:src="'/files/getById?height=600&width=600&file_id=' + file.id"
							:alt="file.file_name"
						>
					</a>
				</div>
			</div>
			<div v-if="(currentPhase === 'NEW' || currentPhase === 'FINISHING') && cancellationData.length > 0" class="cancellation-wrapper">
				<span v-for="cancellation in cancellationData">{{ parseDate(cancellation.date) }} | {{ cancellation.reason }}</span>
			</div>
			<div v-if="supportTicket.phase === 'NEW'" class="actions">
				<Button class="primary action button" @click="incrementPhase" :disabled="isLoading">VOLGENDE</Button>
			</div>
			<in-progress-detail v-if="currentPhase === 'IN_PROGRESS'" :support-ticket="supportTicket" :is-disabled="supportTicket.phase !== 'IN_PROGRESS'" :force-update="forceUpdate"></in-progress-detail>
			<execution-appointment-create-form v-if="currentPhase === 'IN_SOLUTION'" :project-marker="project" :is-disabled="supportTicket.phase !== 'IN_SOLUTION'" :is-reschedule="false" :is-support="true" :support-ticket="supportTicket" class="divided"></execution-appointment-create-form>
			<finish-detail v-if="currentPhase === 'FINISHING' || currentPhase === 'FINISHED'" :support-ticket="supportTicket" :finish-step="finishStep"></finish-detail>
		</div>
	</div>
</template>

<script>
import {mapActions, mapMutations, mapState} from "vuex";
import { parseDate } from "../../utils";
import { client } from "../../store";
import ToggleField from "../ToggleField.vue";
import InProgressDetail from "./InProgressDetail.vue";
import ExecutionAppointmentCreateForm from "../ExecutionAppointmentCreateForm.vue";
import FinishDetail from "./FinishDetail.vue";
import Button from "../Button.vue";

export default {
	name: "SupportTicketDetailModal",
	components: { Button, FinishDetail, ExecutionAppointmentCreateForm, ToggleField, InProgressDetail },

	props: {
		marker: Object,
		currentFilter: String
	},

	data() {
		return {
			currentPhase: null,
			supportTicketType: null,
			project: null,
			appointment: null,
			originalAppointment: null,
			supportTicket: null,
			files: [],
			phaseOrder: {
				"NEW": 0,
				"IN_PROGRESS": 1,
				"IN_SOLUTION": 2,
				"FINISHING": 3,
				"FINISHED": 4
			},
			cancellationData: []
		}
	},

	computed: {
		...mapState({
			isLoading: "loading"
		})
	},

	methods: {
		...mapActions({
			setLoading: "map/setLoading",
			setIdle: "map/setIdle",
			getProjectById: "projects/getProjectById",
			getSupportTicketById: "support_tickets/getSupportTicketById"
		}),
		...mapMutations({
			removeProjectMarker: 'projects/removeProjectMarker'
		}),

		fetchAdditionalInfo() {
			this.setLoading();

			this.getSupportTicketById(this.marker.id).then((result) => {
				try {
					this.supportTicket = Object.assign({}, this.marker, result);
					this.project = result.project;

					if (this.supportTicket.type === "SUPPORT") {
						this.originalAppointment = this.project.appointments.find(appointment => appointment.type === "PROJECT/UITVOERENDE_PLOEG" && appointment.actual_end_time !== null && appointment.deleted_at == null);
					} else if (this.supportTicket.type === "STOPPED_EXECUTION") {
						this.originalAppointment = this.supportTicket.appointment;
					}
					this.appointment = this.supportTicket.resolve_appointment || this.supportTicket.appointment;
					this.currentPhase = this.supportTicket.phase;
					this.cancellationData = JSON.parse(this.supportTicket.cancellation_data);

					this.getFiles(this.project.company.id).then((files) => {
						this.files = files.getFiles;
					}).catch((error) => {
						this.$notify({
							group: "app",
							title: "Fout bij het ophalen van bestanden",
							type: "error",
							text: "Bestanden konden niet opgehaald worden."
						});
					}).finally(() => {
						this.$forceUpdate();
						this.setIdle();
					});

					this.setIdle();
				} catch (error) {
					this.handleError(error);

					this.setIdle();
				}
			});
		},

		getFiles(companyId) {
			let fileType = 'support/support'
			if (this.supportTicket.type === 'STOPPED_EXECUTION') {
				fileType = 'support/afgebroken'
			}

			return new Promise((resolve, reject) => {
				client.query(`
					query($types: [String]!, $company_id: ID!) {
						getFiles(types: $types, company_id: $company_id) {
							id
							company_id
							file_name
							type
							upload_date
							size
						}
					}
				`, {
					company_id: companyId,
					types: [fileType]
				}).then(result => {
					resolve(result);
				}).catch(error => {
					console.error(error);
					reject("Bestanden konden niet worden opgehaald")
				});
			});
		},

		incrementPhase() {
			this.setLoading();

			client.query(`
				mutation($support_ticket_id: ID!) {
					incrementPhase(support_ticket_id: $support_ticket_id)
				}
			`, {
				support_ticket_id: this.supportTicket.id
			}).then(() => {
				this.fetchAdditionalInfo();
			}).catch((error) => {
				this.setIdle();

				this.$notify({
					group: "app",
					title: "Status updaten",
					type: "error",
					text: error
				});
			});
		},

		updateInvoiceOnHold() {
			this.setLoading();
			this.supportTicket.invoice_on_hold = !this.supportTicket.invoice_on_hold;

			client.query(`
				mutation($support_ticket_id: ID!, $invoice_on_hold: Boolean) {
					updateSupportTicket(support_ticket_id: $support_ticket_id, invoice_on_hold: $invoice_on_hold)
				}
			`, {
				support_ticket_id: this.supportTicket.id,
				invoice_on_hold: this.supportTicket.invoice_on_hold
			}).then(() => {
				this.$notify({
					group: "app",
					title: "Factuur op ON-HOLD wijzigen",
					type: "success",
					text: "ON-HOLD succesvol gewijzigd"
				});

				this.setIdle();
				this.forceUpdate();
			}).catch((error) => {
				this.supportTicket.invoice_on_hold = !this.supportTicket.invoice_on_hold

				this.setIdle();

				this.$notify({
					group: "app",
					title: "Factuur op ON-HOLD zetten",
					type: "error",
					text: error
				});
			});
		},

		changeCurrentPhase(phase) {
			if (phase === this.currentPhase || this.phaseOrder[phase] > this.phaseOrder[this.supportTicket.phase]) {
				return;
			}

			this.currentPhase = phase;
			this.$forceUpdate();
		},

		parseDate(date) {
			return parseDate(date);
		},

		forceUpdate() {
			this.fetchAdditionalInfo();
		},

		finishStep(refreshInfo) {
			if (refreshInfo === true) {
				this.fetchAdditionalInfo();
			} else {
				this.$events.emit("map/closePopup");
				this.$emit("close");

				this.removeProjectMarker(this.project.id);
			}
		},

		handleError(error) {
			client.query(`
						mutation($error: String $url: String, $context: String) {
							logError(error: $error, url: $url, context: $context)
						}
					`, {
				error: JSON.stringify(error, Object.getOwnPropertyNames(error)),
				context: JSON.stringify({
					marker: this.marker,
					project: this.project
				}),
				url: '/graphql/query/projectById',
			}).then(result => {
				if (result.logError !== true) {
					console.error('Error logging failed');
				}
			}).catch(error => {
				console.error('Error logging failed');
			});

			console.error(error);
			this.$notify({
				group: "app",
				title: "Informatie ophalen",
				type: "error",
				text: "Er is een fout opgetreden bij het ophalen van de informatie"
			});
		}
	},

	beforeMount() {
		this.supportTicketType = this.currentFilter.includes('support/support') ? 'SUPPORT' : 'STOPPED_EXECUTION';
		if (this.currentFilter === "support/all/finished") {
			this.supportTicketType = null;
		}
		const filterArray = this.currentFilter.split("/");
		this.currentPhase = filterArray[filterArray.length - 1].toUpperCase();
		this.fetchAdditionalInfo();
	},

	mounted() {
		this.$events.on("executionAppointment/created", () => {
			this.$events.off('executionAppointment/created');
			this.fetchAdditionalInfo();
		});
	}
}
</script>

<style scoped lang="scss">
.Modal {
	padding: 28px 24px;
	font-size: 17px;
}

.header {
	border-bottom: 2px solid #d2d2d2;
	padding-bottom: 28px;
	margin-bottom: 24px;

	display: flex;
	justify-content: space-between;
	gap: 40px;
}

.button {
	color: rgb(102, 102, 102);
	background-color: #fff;
	box-shadow: none;
	border: 2px solid #d2d2d2;
	text-align: center;
	padding: 8px 12px;
	border-radius: 4px;
	font-size: 1em;
	display: inline;
	font-weight: 425;
}

.navigation.button {
	width: 100%;
}

.primary.button {
	background-color: #35a5a4;
	color: white;
	border: 2px solid #35a5a4;
}

.actions {
	margin-top: 1em;
	display: flex;
	justify-content: flex-end;
	gap: 24px;
}

.clickable.button {
	border: 2px solid #35a5a4;
	color: #35a5a4;
}

::v-deep .Button {
	margin-bottom: 0;
}

.details {
	padding-bottom: 24px;
}

.detail-columns {
	display: flex;
	justify-content: space-between;
	gap: 24px;

	.detail-column {
		width: 50%;

		.detail-row {

			.row-header {
				color: #35a5a4;
				font-weight: 800;
				margin-bottom: 10px;
			}
			.primary.row-header {
				padding-left: 5px;
				border-left: 3px solid #35a5a4;
			}

			.row-item {
				justify-content: space-between;
				display: flex;
				margin-top: 5px;

				.items {
					display: flex;
					flex-direction: column;
					align-items: flex-end;
					gap: 5px;
				}
			}
			.padded.row-item {
				padding-top: 20px;
			}
		}

		.detail-row ~ .detail-row {
			margin-top: 32px;
		}

		.detail-vertical-row {
			display: flex;
			height: 100%;
			flex-direction: column;
			justify-content: space-between;
			gap: 24px;

			.support-reason {
				border: 2px solid #efbcc4;
				color: #d4495e;
				text-transform: uppercase;
				padding: 8px 0;
			}

			textarea {
				flex-grow: 2;
			}

			.row-item {
				justify-content: space-between;
				display: flex;
			}
		}
	}
}

.pictures {
	display: flex;
	justify-content: space-around;
	gap: 24px;

	.upload-wrapper {
		width: 25%;
		aspect-ratio: 16/9;
	}
}

.divided {
	border-top: 2px solid #d2d2d2;
	margin-top: 24px;
}

textarea {
	width: 100%;
	height: 200px;
	margin: 0;
	border: 2px solid #d2d2d2;
	box-shadow: none;
	border-radius: 4px;
	color: #666;
	background-color: #fff;
}

.cancellation-wrapper {
	display: flex;
	flex-direction: column;
	gap: 4px;
	margin-top: 24px
}
</style>
